import { IUser } from "../utils/userHandler";
import {
  AddDeliveryDTO,
  AddRaceDTO,
  CountByStateResult,
  Race,
  RaceState,
} from "./model";
import ClientApi from "./client-api";
import DriverApi from "./driver-api";
import AdminApi from "./admin-api";
import FCMApi from "./fcm-api";
import CompanyApi from "./company-api";
import BranchApi from "./branch-api";
import ReportApi from "./report-api";
import CategoryAPI from "./category-api";
import ProductAPI from "./product-api";

export class BaseCaller {
  private path: string;
  private token: string | null;
  private handler: (response: any) => boolean;

  constructor(path: string, token: string | null) {
    this.path = path;
    this.token = token;

    this.handleResponse = this.handleResponse.bind(this);
  }

  public setPath(path: string) {
    if (this.path === path) {
      return;
    }
    this.path = path;
    this.token = null;
  }

  public setToken(token: string | null) {
    this.token = token;
  }

  public get(suffix: string): Promise<any> {
    return fetch(`${this.path}/${suffix}`, {
      method: "GET",
      headers: this.buildHeaders(),
    }).then(this.handleResponse.bind(this));
  }

  public post(suffix: string, body: any): Promise<any> {
    return fetch(`${this.path}/${suffix}`, {
      method: "POST",
      headers: this.buildHeaders(),
      body: JSON.stringify(body),
    }).then(this.handleResponse);
  }

  public put(suffix: string, body: any): Promise<any> {
    return fetch(`${this.path}/${suffix}`, {
      method: "PUT",
      headers: this.buildHeaders(),
      body: JSON.stringify(body),
    }).then(this.handleResponse);
  }

  public postForm(suffix: string, body: { [key: string]: any }) {
    const data = new URLSearchParams();
    for (const key of Object.keys(body)) {
      data.append(key, body[key]);
    }

    return fetch(`${this.path}/${suffix}`, {
      method: "post",
      body: data,
    }).then(this.handleResponse);
  }

  public postFormFile(suffix: string, body: File) {
    const data = new FormData();
    data.append("file", body, body.name);
    data.append("user", "hubot");
    // Weird stuff, you don't need to send the content-type to make it work
    return fetch(`${this.path}/${suffix}`, {
      method: "post",
      body: data,
      headers: {
        Accept: "application/json",
        Authorization: this.token || "",
      },
    }).then(this.handleResponse);
  }

  public addHandler(handler: (err: any) => boolean) {
    this.handler = handler;
  }

  /**
   * Builds the common headers to use the API
   */
  private buildHeaders() {
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
      Authorization: "",
    };

    if (this.token) {
      headers.Authorization = this.token;
    }
    return headers;
  }

  /**
   * Handle the server response.
   *
   * This check for the status code, and always try to return an JSON object.
   *
   * @param {Response} response
   */
  private handleResponse(response: any) {
    if (response.headers.get("Content-Type") === "application/octet-stream") {
      return response.blob();
    }

    if (response.status >= 200 && response.status <= 300) {
      return response.json();
    }

    if (this.handler && this.handler(response)) {
      console.warn("without handler, returning ok when the result is not ok");
      return;
    }

    if (response.status === 401) {
      throw new Error(response.status);
    }

    return response.json().then((data: any) => {
      const error: any = new Error(response.statusText || response.status);
      error.status = response.status;
      error.statusText = response.statusText;
      error.response = data;
      return Promise.reject(error);
    });
  }
}

class RaceAPI {
  private caller: BaseCaller;

  constructor(caller: BaseCaller) {
    this.caller = caller;
  }

  public byId(id: string): Promise<Race> {
    return this.caller.get(`opportunity/${id}`);
  }

  public all(): any {
    return this.caller.get(`opportunity/admin`);
  }

  public add(data: AddRaceDTO): Promise<Race> {
    return this.caller.post("admin/addOpportunity", data);
  }

  public listByState(state: RaceState): Promise<CountByStateResult[]> {
    return this.caller.get(`admin/opportunity/byState?state=${state}`);
  }

  public countByState(): Promise<CountByStateResult[]> {
    return this.caller.get("admin/opportunity/state");
  }

  public cancel(id: number, observation: string): Promise<Race> {
    return this.caller.post(`opportunity/${id}/cancel`, observation);
  }

  public assign(id: number, driver: number): Promise<Race> {
    return this.caller.post(`admin/opportunity/assignPermissive`, {
      race: id,
      driver: driver,
    });
  }

  public changeBranch(id: number, branch: number) {
    return this.caller.post("admin/opportunity/updateBranch", {
      race: id,
      branch: branch,
    });
  }

  public addDelivery(deliveryData: AddDeliveryDTO): Promise<Race> {
    return this.caller.post("admin/addDelivery", deliveryData);
  }
}

class Auth {
  private caller: BaseCaller;

  constructor(caller: BaseCaller) {
    this.caller = caller;
  }

  public login(user: string, pass: string): Promise<IUser> {
    return this.caller.postForm("admin/login", {
      username: user,
      password: pass,
    });
  }
}

class OrderAPI {
  private caller: BaseCaller;

  constructor(caller: BaseCaller) {
    this.caller = caller;
  }

  public byId(id: string): Promise<Race> {
    return this.caller.get(`opportunity/${id}`);
  }

  public byRaceId(id: string): Promise<any> {
    return this.caller.get(`order/byRace/${id}`);
  }
}

export class Api {
  private readonly apiUrl: string;
  private readonly caller: BaseCaller;

  constructor(url: string) {
    this.apiUrl = url;
    this.caller = new BaseCaller(this.apiUrl, null);
  }

  public notifyLogin(token: string) {
    this.caller.setToken(token);
  }

  public notifyNewUrl(path: string): any {
    this.caller.setPath(path);
  }

  public notifyLogout() {
    this.caller.setToken(null);
  }

  public addErrorHandler(handler: (err: any) => boolean) {
    this.caller.addHandler(handler);
  }

  /**
   * Get the api to call auth functions.
   * @returns {Auth}
   */
  public auth() {
    return new Auth(this.caller);
  }

  public race() {
    return new RaceAPI(this.caller);
  }

  public client() {
    return new ClientApi(this.caller);
  }

  public driver() {
    return new DriverApi(this.caller);
  }

  public admin() {
    return new AdminApi(this.caller);
  }

  public fcm() {
    return new FCMApi(this.caller);
  }

  public company() {
    return new CompanyApi(this.caller);
  }

  public branch() {
    return new BranchApi(this.caller);
  }

  public report() {
    return new ReportApi(this.caller);
  }

  public order() {
    return new OrderAPI(this.caller);
  }

  public category() {
    return new CategoryAPI(this.caller);
  }

  public product() {
    return new ProductAPI(this.caller);
  }
}



// WEBPACK FOOTER //
// ./src/api/api.ts